import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box, Container, Typography, Button, IconButton,
    TextField, InputAdornment, Divider, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
    ArrowBackOutlined, CheckCircleOutline,
    NorthEastOutlined, SouthWestOutlined, InfoOutlined,
    TrendingUpOutlined, TrendingDownOutlined, LockOutlined,
} from '@mui/icons-material';
import {
    AreaChart, Area, XAxis, YAxis, Tooltip,
    ResponsiveContainer, CartesianGrid, ReferenceLine,
} from 'recharts';
import { B, NGX_STOCKS } from './investData';

/* ─── Custom tooltip ───────────────────────────────────────────── */
const DarkTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    return (
        <Box sx={{ background: B.dark, border: `1px solid ${alpha('#fff', .08)}`, borderRadius: '10px', px: 1.5, py: 1, boxShadow: `0 8px 24px ${alpha(B.dark, .5)}` }}>
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '.8rem' }}>₦{payload[0].value.toLocaleString()}</Typography>
        </Box>
    );
};

/* ─── Component ─────────────────────────────────────────────────── */
const StockDetail: React.FC = () => {
    const { id }   = useParams<{ id: string }>();
    const navigate = useNavigate();
    const theme    = useTheme();
    const isDark   = theme.palette.mode === 'dark';

    const stock = NGX_STOCKS.find(s => s.id === id);

    const [step, setStep]         = useState<'detail' | 'order' | 'confirm' | 'success'>('detail');
    const [side, setSide]         = useState<'buy' | 'sell'>('buy');
    const [units, setUnits]       = useState('');
    const [period, setPeriod]     = useState<'1W' | '1M' | '3M' | '6M' | '1Y'>('1M');

    const pageBg     = isDark ? B.deep : B.lavender;
    const surfaceBg  = isDark ? alpha(B.mid, .55) : '#fff';
    const surfaceBdr = `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`;
    const surfaceShd = `0 4px 28px ${alpha(B.dark, isDark ? .25 : .06)}`;
    const textPri    = isDark ? '#fff' : B.ink;
    const textMid    = alpha(isDark ? '#fff' : B.ink, .45);
    const textFaint  = alpha(isDark ? '#fff' : B.ink, .28);
    const dividerC   = alpha(isDark ? '#fff' : B.dark, .08);
    const glass      = { background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd, borderRadius: '20px', backdropFilter: isDark ? 'blur(20px)' : 'none' };

    if (!stock) {
        return (
            <Box sx={{ minHeight: '100vh', background: pageBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ color: textMid }}>Stock not found.</Typography>
            </Box>
        );
    }

    const positive      = stock.change > 0;
    const accentColor   = positive ? B.green : B.red;
    const chartData     = stock.chart.map((v, i) => ({ i: `D${i + 1}`, v }));
    const numUnits      = parseFloat(units) || 0;
    const orderTotal    = numUnits * stock.price;
    const sideColor     = side === 'buy' ? B.green : B.red;

    /* ── Success screen ── */
    if (step === 'success') {
        return (
            <Box sx={{ minHeight: '100vh', background: pageBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Sans', system-ui, sans-serif" }}>
                <Box sx={{ textAlign: 'center', px: 4 }}>
                    <Box sx={{ width: 80, height: 80, borderRadius: '50%', background: alpha(sideColor, .12), border: `2px solid ${alpha(sideColor, .3)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
                        <CheckCircleOutline sx={{ color: sideColor, fontSize: 40 }} />
                    </Box>
                    <Typography sx={{ color: textPri, fontFamily: "'Instrument Serif', serif", fontSize: '1.6rem', mb: .75 }}>
                        Order {side === 'buy' ? 'placed' : 'executed'}!
                    </Typography>
                    <Typography sx={{ color: textMid, fontSize: '.85rem', mb: .5 }}>
                        {side === 'buy' ? 'Bought' : 'Sold'} {numUnits} units of
                    </Typography>
                    <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.95rem', mb: .5 }}>{stock.name}</Typography>
                    <Typography sx={{ color: textFaint, fontSize: '.82rem', mb: 4 }}>
                        Total: ₦{orderTotal.toLocaleString()} · Settles T+3
                    </Typography>
                    <Button fullWidth variant="contained"
                            onClick={() => navigate('/invest?tab=stocks')}
                            sx={{ background: `linear-gradient(135deg, ${B.purple}, ${B.light})`, borderRadius: '14px', textTransform: 'none', fontWeight: 700, py: 1.5, boxShadow: 'none' }}>
                        Back to Investments
                    </Button>
                    <Button fullWidth onClick={() => { setStep('detail'); setUnits(''); }}
                            sx={{ mt: 1, color: textMid, textTransform: 'none', fontWeight: 600 }}>
                        New Order
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', background: pageBg, pb: 12, fontFamily: "'DM Sans', system-ui, sans-serif" }}>

            {/* ══ HERO ══════════════════════════════════════════════ */}
            <Box sx={{
                background: `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 60%, ${alpha(stock.color, .55)} 100%)`,
                mt: { xs: '-76px', sm: 0 }, pt: { xs: '80px', sm: '28px' }, pb: { xs: 4, sm: 14 },
                px: 3, position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -60, right: -40, width: 220, height: 220, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(stock.color, .2)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

                <Box display="flex" alignItems="center" gap={1.5} mb={3}>
                    <IconButton size="small" onClick={() => step === 'detail' ? navigate('/invest?tab=stocks') : setStep('detail')}
                                sx={{ color: alpha('#fff', .6), background: alpha('#fff', .08), '&:hover': { background: alpha('#fff', .16) } }}>
                        <ArrowBackOutlined sx={{ fontSize: 18 }} />
                    </IconButton>
                    <Box sx={{ width: 40, height: 40, borderRadius: '11px', background: alpha(stock.color, .18), border: `1px solid ${alpha(stock.color, .3)}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography sx={{ color: stock.color, fontWeight: 800, fontSize: '.58rem', textAlign: 'center', lineHeight: 1.1 }}>
                            {stock.ticker.slice(0, 5)}
                        </Typography>
                    </Box>
                    <Box flex={1}>
                        <Typography sx={{ color: alpha('#fff', .4), fontSize: '.62rem', letterSpacing: '.1em', textTransform: 'uppercase' }}>{stock.sector}</Typography>
                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '.95rem' }}>{stock.name}</Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
                    <Box>
                        <Typography sx={{ color: '#fff', fontFamily: "'Instrument Serif', serif", fontSize: '2.2rem', lineHeight: 1 }}>
                            ₦{stock.price.toLocaleString()}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: .5, mt: .75 }}>
                            {positive
                                ? <NorthEastOutlined sx={{ color: B.green, fontSize: 14 }} />
                                : <SouthWestOutlined sx={{ color: B.red, fontSize: 14 }} />
                            }
                            <Typography sx={{ color: accentColor, fontWeight: 700, fontSize: '.82rem' }}>
                                {positive ? '+' : ''}{stock.changeAbs.toFixed(2)} ({positive ? '+' : ''}{stock.change}%)
                            </Typography>
                            <Typography sx={{ color: alpha('#fff', .3), fontSize: '.72rem' }}>today</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ px: 1.5, py: .5, borderRadius: 20, background: alpha(accentColor, .15), border: `1px solid ${alpha(accentColor, .3)}` }}>
                        <Typography sx={{ color: accentColor, fontWeight: 700, fontSize: '.7rem' }}>NGX · {stock.ticker}</Typography>
                    </Box>
                </Box>
            </Box>

            <Container maxWidth="sm" sx={{ mt: { xs: 2, sm: -8 }, position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>

                {/* ════════════════════════════════════════════
                    DETAIL VIEW
                ════════════════════════════════════════════ */}
                {step === 'detail' && (
                    <>
                        {/* Key stats strip */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mb: 2 }}>
                            {[
                                { label: 'Mkt Cap',    value: stock.marketCap  },
                                { label: 'P/E Ratio',  value: stock.peRatio    },
                                { label: 'Dividend',   value: `${stock.dividend}%` },
                            ].map(s => (
                                <Box key={s.label} sx={{ ...glass, p: 1.5, textAlign: 'center' }}>
                                    <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.84rem' }}>{s.value}</Typography>
                                    <Typography sx={{ color: textFaint, fontSize: '.58rem', mt: .2 }}>{s.label}</Typography>
                                </Box>
                            ))}
                        </Box>

                        {/* Chart */}
                        <Box sx={{ ...glass, p: 2.5, mb: 2 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.88rem' }}>Price Chart</Typography>
                                <Box sx={{ display: 'flex', gap: .5 }}>
                                    {(['1W', '1M', '3M', '6M', '1Y'] as const).map(p => (
                                        <Box key={p} onClick={() => setPeriod(p)} sx={{
                                            px: 1, py: .35, borderRadius: 8, cursor: 'pointer',
                                            background: period === p ? `linear-gradient(135deg, ${B.purple}, ${B.light})` : 'transparent',
                                            border: `1px solid ${period === p ? 'transparent' : alpha(isDark ? '#fff' : B.dark, .1)}`,
                                        }}>
                                            <Typography sx={{ color: period === p ? '#fff' : textFaint, fontSize: '.62rem', fontWeight: 700 }}>{p}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                            <ResponsiveContainer width="100%" height={170}>
                                <AreaChart data={chartData}>
                                    <defs>
                                        <linearGradient id={`sg-${stock.id}`} x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%"  stopColor={accentColor} stopOpacity={0.22} />
                                            <stop offset="95%" stopColor={accentColor} stopOpacity={0}    />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke={alpha(isDark ? '#fff' : B.dark, .05)} vertical={false} />
                                    <XAxis dataKey="i" tick={{ fill: textFaint, fontSize: 9 }} axisLine={false} tickLine={false} />
                                    <YAxis domain={['auto', 'auto']} tick={{ fill: textFaint, fontSize: 9 }} axisLine={false} tickLine={false} width={40} tickFormatter={v => `₦${(v/1000).toFixed(0)}k`} />
                                    <Tooltip content={<DarkTooltip />} />
                                    <ReferenceLine y={stock.chart[0]} stroke={alpha(isDark ? '#fff' : B.dark, .1)} strokeDasharray="4 4" />
                                    <Area type="monotone" dataKey="v" stroke={accentColor} strokeWidth={2} fill={`url(#sg-${stock.id})`} dot={false} />
                                </AreaChart>
                            </ResponsiveContainer>

                            {/* 52-week range */}
                            <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${dividerC}` }}>
                                <Box display="flex" justifyContent="space-between" mb={.75}>
                                    <Typography sx={{ color: textFaint, fontSize: '.65rem' }}>52-week low: ₦{stock.low52.toLocaleString()}</Typography>
                                    <Typography sx={{ color: textFaint, fontSize: '.65rem' }}>High: ₦{stock.high52.toLocaleString()}</Typography>
                                </Box>
                                <Box sx={{ height: 4, borderRadius: 4, background: alpha(isDark ? '#fff' : B.dark, .08), position: 'relative', overflow: 'visible' }}>
                                    <Box sx={{
                                        position: 'absolute', top: 0, left: 0,
                                        width: `${((stock.price - stock.low52) / (stock.high52 - stock.low52)) * 100}%`,
                                        height: '100%', borderRadius: 4,
                                        background: `linear-gradient(90deg, ${B.purple}, ${accentColor})`,
                                    }} />
                                    <Box sx={{
                                        position: 'absolute', top: '50%', transform: 'translate(-50%, -50%)',
                                        left: `${((stock.price - stock.low52) / (stock.high52 - stock.low52)) * 100}%`,
                                        width: 10, height: 10, borderRadius: '50%',
                                        background: accentColor, border: `2px solid ${surfaceBg}`,
                                        boxShadow: `0 0 8px ${alpha(accentColor, .6)}`,
                                    }} />
                                </Box>
                            </Box>
                        </Box>

                        {/* About */}
                        <Box sx={{ ...glass, p: 2.5, mb: 2 }}>
                            <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.88rem', mb: 1.25 }}>About {stock.name}</Typography>
                            <Typography sx={{ color: textMid, fontSize: '.8rem', lineHeight: 1.7 }}>{stock.description}</Typography>
                            <Divider sx={{ my: 2, borderColor: dividerC }} />
                            {[
                                { label: 'Exchange',     value: 'NGX (Nigerian Exchange)'     },
                                { label: 'Ticker',       value: stock.ticker                  },
                                { label: 'Sector',       value: stock.sector                  },
                                { label: 'Market Cap',   value: stock.marketCap               },
                                { label: 'P/E Ratio',    value: stock.peRatio                 },
                                { label: 'Div. Yield',   value: `${stock.dividend}%`          },
                                { label: 'Settlement',   value: 'T+3 Business Days'           },
                            ].map(({ label, value }) => (
                                <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: `1px solid ${dividerC}`, '&:last-child': { borderBottom: 'none' } }}>
                                    <Typography sx={{ color: textMid, fontSize: '.78rem' }}>{label}</Typography>
                                    <Typography sx={{ color: textPri, fontWeight: 600, fontSize: '.78rem' }}>{value}</Typography>
                                </Box>
                            ))}
                        </Box>

                        {/* Buy / Sell CTAs */}
                        <Box display="flex" gap={1.5}>
                            <Button fullWidth variant="contained"
                                    onClick={() => { setSide('buy'); setStep('order'); }}
                                    sx={{ background: `linear-gradient(135deg, ${B.green}, ${alpha(B.green, .7)})`, borderRadius: '14px', textTransform: 'none', fontWeight: 700, fontSize: '.95rem', py: 1.6, boxShadow: 'none', '&:hover': { boxShadow: `0 8px 24px ${alpha(B.green, .35)}` } }}>
                                Buy
                            </Button>
                            <Button fullWidth variant="outlined"
                                    onClick={() => { setSide('sell'); setStep('order'); }}
                                    sx={{ borderColor: B.red, color: B.red, borderRadius: '14px', textTransform: 'none', fontWeight: 700, fontSize: '.95rem', py: 1.6, '&:hover': { background: alpha(B.red, .06), borderColor: B.red } }}>
                                Sell
                            </Button>
                        </Box>
                    </>
                )}

                {/* ════════════════════════════════════════════
                    ORDER STEP
                ════════════════════════════════════════════ */}
                {step === 'order' && (
                    <>
                        {/* Buy/Sell toggle */}
                        <Box sx={{ ...glass, p: .6, mb: 2, display: 'flex', gap: .5 }}>
                            {(['buy', 'sell'] as const).map(s => {
                                const active = side === s;
                                const c = s === 'buy' ? B.green : B.red;
                                return (
                                    <Box key={s} onClick={() => setSide(s)} sx={{
                                        flex: 1, py: 1.2, borderRadius: '14px', cursor: 'pointer', textAlign: 'center',
                                        background: active ? alpha(c, .14) : 'transparent',
                                        border: `1px solid ${active ? alpha(c, .3) : 'transparent'}`,
                                        transition: 'all .18s',
                                    }}>
                                        <Typography sx={{ color: active ? c : textFaint, fontWeight: 700, fontSize: '.82rem', textTransform: 'capitalize' }}>
                                            {s} {stock.ticker}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>

                        <Box sx={{ ...glass, p: 2.5, mb: 2 }}>
                            <Box display="flex" justifyContent="space-between" mb={2}>
                                <Box>
                                    <Typography sx={{ color: textFaint, fontSize: '.65rem' }}>Current Price</Typography>
                                    <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.95rem' }}>₦{stock.price.toLocaleString()}</Typography>
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                    <Typography sx={{ color: textFaint, fontSize: '.65rem' }}>Change</Typography>
                                    <Typography sx={{ color: accentColor, fontWeight: 700, fontSize: '.95rem' }}>
                                        {positive ? '+' : ''}{stock.change}%
                                    </Typography>
                                </Box>
                            </Box>

                            <Typography sx={{ color: textMid, fontSize: '.78rem', mb: 1.5 }}>Number of units</Typography>
                            <TextField
                                fullWidth
                                placeholder="0"
                                type="number"
                                value={units}
                                onChange={e => setUnits(e.target.value.replace(/[^0-9]/g, ''))}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end"><Typography sx={{ color: textFaint, fontSize: '.78rem' }}>units</Typography></InputAdornment>,
                                    sx: {
                                        color: textPri, fontWeight: 700, fontSize: '1.3rem',
                                        borderBottom: `2px solid ${units ? sideColor : alpha(isDark ? '#fff' : B.dark, .15)}`,
                                        '&:before, &:after': { display: 'none' },
                                    },
                                }}
                                variant="standard"
                                sx={{ mb: 2 }}
                            />

                            {/* Quick unit amounts */}
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                {[1, 5, 10, 20].map(q => (
                                    <Box key={q} onClick={() => setUnits(String(q))} sx={{
                                        flex: 1, py: .6, borderRadius: 20, cursor: 'pointer', textAlign: 'center',
                                        background: numUnits === q ? alpha(sideColor, .1) : alpha(isDark ? '#fff' : B.dark, .05),
                                        border: `1px solid ${numUnits === q ? alpha(sideColor, .28) : alpha(isDark ? '#fff' : B.dark, .1)}`,
                                        transition: 'all .15s',
                                    }}>
                                        <Typography sx={{ color: numUnits === q ? sideColor : textMid, fontSize: '.72rem', fontWeight: 700 }}>{q}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        {/* Order summary */}
                        {numUnits > 0 && (
                            <Box sx={{ ...glass, p: 2.5, mb: 2, border: `1px solid ${alpha(sideColor, .18)}` }}>
                                <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.85rem', mb: 1.5 }}>Order Summary</Typography>
                                {[
                                    { label: 'Price per unit',  value: `₦${stock.price.toLocaleString()}`      },
                                    { label: 'Units',           value: numUnits                                 },
                                    { label: 'Broker fee',      value: `₦${(orderTotal * 0.0075).toFixed(2)}`  },
                                    { label: 'SEC levy',        value: `₦${(orderTotal * 0.003).toFixed(2)}`   },
                                    { label: 'Total',           value: `₦${orderTotal.toLocaleString()}`        },
                                ].map(({ label, value }) => (
                                    <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: .9, borderBottom: `1px solid ${dividerC}`, '&:last-child': { borderBottom: 'none' } }}>
                                        <Typography sx={{ color: textMid, fontSize: '.78rem' }}>{label}</Typography>
                                        <Typography sx={{ color: label === 'Total' ? sideColor : textPri, fontWeight: label === 'Total' ? 700 : 600, fontSize: '.78rem' }}>{value}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}

                        <Button fullWidth variant="contained"
                                disabled={numUnits <= 0}
                                onClick={() => setStep('confirm')}
                                sx={{
                                    background: side === 'buy'
                                        ? `linear-gradient(135deg, ${B.green}, ${alpha(B.green, .7)})`
                                        : `linear-gradient(135deg, ${B.red}, ${alpha(B.red, .7)})`,
                                    borderRadius: '14px', textTransform: 'none', fontWeight: 700, fontSize: '.95rem',
                                    py: 1.6, boxShadow: 'none',
                                    '&:disabled': { background: alpha(B.mid, .4), color: alpha('#fff', .3) },
                                }}>
                            Review Order
                        </Button>
                    </>
                )}

                {/* ════════════════════════════════════════════
                    CONFIRM STEP
                ════════════════════════════════════════════ */}
                {step === 'confirm' && (
                    <>
                        <Box sx={{ ...glass, p: 2.5, mb: 2 }}>
                            <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.88rem', mb: 2 }}>Confirm Order</Typography>

                            {[
                                { label: 'Action',      value: side === 'buy' ? 'Buy (Market Order)' : 'Sell (Market Order)' },
                                { label: 'Stock',       value: `${stock.name} (${stock.ticker})`    },
                                { label: 'Units',       value: numUnits                               },
                                { label: 'Price',       value: `₦${stock.price.toLocaleString()}`    },
                                { label: 'Total',       value: `₦${orderTotal.toLocaleString()}`      },
                                { label: side === 'buy' ? 'Debit from' : 'Credit to', value: 'NG Credit Wallet' },
                                { label: 'Settlement',  value: 'T+3 Business Days'                   },
                            ].map(({ label, value }) => (
                                <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 1.1, borderBottom: `1px solid ${dividerC}`, '&:last-child': { borderBottom: 'none' } }}>
                                    <Typography sx={{ color: textMid, fontSize: '.78rem' }}>{label}</Typography>
                                    <Typography sx={{ color: label === 'Action' ? sideColor : textPri, fontWeight: 600, fontSize: '.78rem', maxWidth: '55%', textAlign: 'right' }}>{value}</Typography>
                                </Box>
                            ))}
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 3, p: 1.75, borderRadius: '14px', background: alpha(B.orange, .06), border: `1px solid ${alpha(B.orange, .18)}` }}>
                            <InfoOutlined sx={{ color: B.orange, fontSize: 16, mt: .1, flexShrink: 0 }} />
                            <Typography sx={{ color: alpha(isDark ? '#fff' : B.ink, .55), fontSize: '.74rem', lineHeight: 1.6 }}>
                                Orders are executed at best available market price on the NGX. Prices may vary slightly at execution. Broker fees and SEC levy apply per transaction.
                            </Typography>
                        </Box>

                        <Button fullWidth variant="contained"
                                onClick={() => setStep('success')}
                                sx={{
                                    background: side === 'buy'
                                        ? `linear-gradient(135deg, ${B.green}, ${alpha(B.green, .7)})`
                                        : `linear-gradient(135deg, ${B.red}, ${alpha(B.red, .7)})`,
                                    borderRadius: '14px', textTransform: 'none', fontWeight: 700, fontSize: '.95rem',
                                    py: 1.6, boxShadow: 'none', mb: 1.5,
                                }}>
                            Confirm {side === 'buy' ? 'Purchase' : 'Sale'}
                        </Button>
                        <Button fullWidth onClick={() => setStep('order')}
                                sx={{ color: textMid, textTransform: 'none', fontWeight: 600 }}>
                            Go Back
                        </Button>
                    </>
                )}

                <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .75 }}>
                    <LockOutlined sx={{ fontSize: 11, color: alpha(isDark ? '#fff' : B.ink, .18) }} />
                    <Typography sx={{ fontSize: '.62rem', color: alpha(isDark ? '#fff' : B.ink, .18), letterSpacing: '.05em' }}>
                        NGX REGULATED · SEC LICENSED · CSCS CUSTODY
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default StockDetail;